<script setup>
import { computed } from 'vue';
import XlCard from '../xl/XlCard.vue';
import XlTag from '../xl/XlTag.vue';
import XlTooltip from '../xl/XlTooltip.vue';

/**
 * What the sequence being written will actually do, in order.
 *
 * A cadence is only correct in relation to its own steps — "90 days before"
 * means nothing until you can see it sits after "120 days before" — so the
 * steps are restated here as a running order rather than left as a set of
 * form rows.
 */
const STOP_REASONS = {
  renewal: ['Renewed', 'Cancelled', 'Excluded', 'Paused'],
  scratch: ['Unsubscribed', 'Excluded', 'Paused'],
};

const props = defineProps({
  rows: { type: Array, default: () => [] },
  segmentNames: { type: Array, default: () => [] },
  recipientCount: { type: Number, default: null },
  anchor: { type: String, default: 'renewal' },
  span: { type: String, default: '' },
});

const audience = computed(() => {
  const who = props.segmentNames.length ? props.segmentNames.join(', ') : 'Pick at least one segment.';
  if (props.recipientCount === null) return who;

  const n = Number(props.recipientCount).toLocaleString('en-US');
  return `${who} · ${n} ${props.recipientCount === 1 ? 'policyholder' : 'policyholders'}`;
});

const stops = computed(() => STOP_REASONS[props.anchor] ?? STOP_REASONS.renewal);
</script>

<template>
  <XlCard padded class="run" data-testid="sequence-run">
    <b class="run__heading">How it will run</b>
    <p class="run__audience" data-testid="sequence-run-audience">{{ audience }}</p>

    <ol v-if="props.rows.length" class="run__steps" data-testid="sequence-run-steps">
      <li v-for="(row, index) in props.rows" :key="index" class="run__step">
        <span class="run__num">{{ index + 1 }}</span>
        <span class="run__what">
          <b><XlTooltip v-if="row.tip" :text="row.tip" underline>{{ row.title }}</XlTooltip><template v-else>{{ row.title }}</template></b>
          <span>{{ row.detail }}</span>
        </span>
      </li>
    </ol>
    <p v-else class="run__empty" data-testid="sequence-run-empty">Nothing to send yet.</p>

    <p v-if="props.span" class="run__span" data-testid="sequence-run-span">{{ props.span }}</p>

    <div class="run__stops">
      <b class="run__sub">Stops automatically when</b>
      <div class="run__chips" data-testid="sequence-run-stops">
        <XlTag v-for="reason in stops" :key="reason" tone="success">{{ reason }}</XlTag>
      </div>
      <p v-if="props.anchor === 'scratch'" class="run__note">
        No renewal to stop against — this one runs to the end unless somebody opts out.
      </p>
    </div>
  </XlCard>
</template>

<style scoped lang="scss">
.run {
  &__heading { font-size: 15px; color: var(--xl-color-primary-text); }

  &__audience {
    margin: 6px 0 16px;
    font-size: 13.5px;
    color: var(--xl-color-outline);
  }

  &__steps {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__step {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 0;
    border-bottom: 1px solid var(--xl-color-border-subtle);
  }

  // 34px tall, but the tile's 42px flex basis outranks its width: the board
  // draws a rounded oblong, not a square.
  &__num {
    display: grid;
    place-items: center;
    flex: 0 0 42px;
    height: 34px;
    border-radius: 13px;
    background: var(--xl-color-primary-container);
    color: var(--xl-color-primary);
    font-size: 13px;
    font-weight: 600;
  }

  &__what {
    display: flex;
    flex-direction: column;
    min-width: 0;

    > b { font-size: 14px; color: var(--xl-color-primary-text); }
    > span { font-size: 13px; line-height: 22px; color: var(--xl-color-outline); }
  }

  &__empty {
    margin: 0;
    font-size: 13.5px;
    color: var(--xl-color-outline);
  }

  &__span {
    margin: 12px 0 0;
    font-size: 12.5px;
    color: var(--xl-color-outline);
  }

  &__note {
    margin: 9px 0 0;
    font-size: 12px;
    color: var(--xl-color-outline);
  }

  &__stops {
    margin-top: 14px;
    padding: 8px 22px;
    border: 1px solid var(--xl-color-border);
    border-radius: var(--xl-radius-lg);
    background: var(--xl-color-surface);
  }

  &__sub { font-size: 13px; color: var(--xl-color-primary-text); }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
}
</style>
